import React, { useState } from 'react';
import { AlertTriangle, TrendingUp, Shield, Activity } from 'lucide-react';
import RiskMatrix from './RiskMatrix';
import RiskList from './RiskList';
import RiskForm from './RiskForm';
import RiskMetrics from './RiskMetrics';
import { useRisks } from '../../hooks/useRisks';
import { Risk, RiskCategory } from '../../types/risk';
import { useDeals } from '../../store/DealsContext';

export default function RiskDashboard() {
  const { deals } = useDeals();
  const [selectedDeal, setSelectedDeal] = useState<string>('');
  const [showRiskForm, setShowRiskForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<RiskCategory | ''>('');
  
  const {
    risks,
    addRisk,
    updateRisk,
    deleteRisk,
    loading
  } = useRisks(selectedDeal);

  const riskCategories: RiskCategory[] = [
    'financial',
    'operational',
    'legal',
    'strategic',
    'regulatory',
    'integration'
  ];

  const filteredRisks = risks.filter(risk => 
    !selectedCategory || risk.category === selectedCategory
  );

  const riskMetrics = {
    total: risks.length,
    high: risks.filter(r => r.impact === 'high' && r.probability === 'high').length,
    mitigated: risks.filter(r => r.status === 'mitigated').length,
    byCategory: riskCategories.reduce((acc, category) => ({
      ...acc,
      [category]: risks.filter(r => r.category === category).length
    }), {} as Record<RiskCategory, number>)
  };

  return (
    <div className="h-full space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Risk Assessment</h2>
          <p className="mt-2 text-gray-600">Monitor and manage deal risks</p>
        </div>
        
        <div className="flex items-center space-x-4">
          <select
            value={selectedDeal}
            onChange={(e) => setSelectedDeal(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Deals</option>
            {deals.map((deal) => (
              <option key={deal.id} value={deal.id}>{deal.name}</option>
            ))}
          </select>
          
          <button
            onClick={() => setShowRiskForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Risk
          </button>
        </div>
      </div>

      <RiskMetrics metrics={riskMetrics} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Risk Matrix</h3>
          </div>
          <div className="p-6">
            <RiskMatrix risks={filteredRisks} onRiskSelect={(risk) => {
              setShowRiskForm(true);
            }} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Risk Distribution</h3>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {riskCategories.map((category) => {
                const categoryRisks = risks.filter(r => r.category === category);
                const highRisks = categoryRisks.filter(r => 
                  r.impact === 'high' && r.probability === 'high'
                ).length;
                
                return (
                  <div key={category} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-600 capitalize">
                          {category}
                        </span>
                        <span className="text-sm text-gray-500">
                          {categoryRisks.length} risks
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-200 rounded-full">
                        <div
                          className="h-2 bg-blue-600 rounded-full"
                          style={{
                            width: `${(categoryRisks.length / risks.length) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                    {highRisks > 0 && (
                      <span className="ml-4 px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">
                        {highRisks} high
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">Risk Register</h3>
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value as RiskCategory | '')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All Categories</option>
            {riskCategories.map((category) => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
        
        <RiskList
          risks={filteredRisks}
          onRiskUpdate={updateRisk}
          onRiskDelete={deleteRisk}
        />
      </div>

      {showRiskForm && (
        <RiskForm
          onClose={() => setShowRiskForm(false)}
          onSubmit={addRisk}
          deals={deals}
        />
      )}
    </div>
  );
}